// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import produce, {Draft} from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';

import {DisplayBookmark} from 'common/report/dashboards/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {QueryReason} from 'common/report/util/query-tracking/types';
import * as bookmarkTypes from 'common/types/reports/bookmark';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {ProjectFeatureFlag} from 'common/report/constants';
import {LocalizationStore} from 'common/i18n/store';

import {hasValidBookmarkParams} from './validation';
import {syncMetricsAcrossBookmark} from './hydrate-bookmark';
import {BookmarkDragDropProvider} from './drag-drop/provider';

export type ParamsMutator = (s: Draft<bookmarkTypes.InsightsBookmarkParams>) => void;

type Bookmark = DisplayBookmark<bookmarkTypes.InsightsBookmarkParams>;
export type ReportState = Bookmark & {
  /** provides an immutable setter on bookmark params  */
  updateParams: (fn: ParamsMutator | bookmarkTypes.InsightsBookmarkParams, options?: ApplyParamsDetail) => void;
  isValid: () => boolean;
};

/** Options passed on every UI interaction that leads to a bookmark param update */
export type ApplyParamsDetail = {queryReason?: QueryReason; requery?: boolean; hasChangedChartType?: boolean};

export type ApplyParamsFunction = (params: bookmarkTypes.InsightsBookmarkParams, options?: ApplyParamsDetail) => void;

/**
 * Provides a Panel context that hosts a @type{Bookmark} instance and utilities to apply immutable modifiers
 *
 * This context also provides an instance of @type{ReportState} which is propagated as a React context value
 * inside @see{useReportBookmark}. This is done to avoid propagating Panel context internals to React components
 * in React hooks.
 */
export class ReportBookmarkManager {
  private declare _onApplyParams: ApplyParamsFunction;
  private _bookmark: Bookmark | null = null;

  private metrics: MetricsResource = null;
  private behaviors: BehaviorsResource = null;
  private currentProject: CurrentProjectResource = null;
  private i18n: LocalizationStore = null;

  applyParams(params, options) {
    this._onApplyParams(params, options);
  }

  updateBookmark(bookmark: Bookmark) {
    const hasInsightsBookmark = `sections` in (this._bookmark?.params ?? {});
    this._bookmark = produce(bookmark, (draft) => {
      /**
       * makes sure any edits to saved entities are reflected in the bookmark
       * this isn't necessary when loading a bookmark since the backend
       * will return a fully hydrated bookmark
       */
      if (`sections` in draft.params) {
        draft.params = syncMetricsAcrossBookmark({
          params: draft.params,
          previousParams: hasInsightsBookmark ? this.bookmark.params : null,
          metrics: this.metrics,
          behaviors: this.behaviors,
          i18n: this.i18n,
          isFirstClassMetricsEnabled: this.currentProject
            .get()
            .value?.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics),
        });
      }
    });
  }

  public syncBookmark(bookmark: Bookmark) {
    this.updateBookmark(bookmark);
    return this.bookmark;
  }

  get bookmark() {
    return this._bookmark;
  }

  public setup(
    onApplyParams: ApplyParamsFunction,
    {
      metrics,
      behaviors,
      i18n,
      currentProject,
    }: {
      metrics: MetricsResource;
      behaviors: BehaviorsResource;
      currentProject: CurrentProjectResource;
      i18n: LocalizationStore;
    },
  ) {
    this._onApplyParams = onApplyParams;
    this.metrics = metrics;
    this.behaviors = behaviors;
    this.i18n = i18n;
    this.currentProject = currentProject;
  }

  public teardown() {
    this._onApplyParams = null;
    this._bookmark = null;
  }
}

export const ReportBookmarkManagerContext = React.createContext<ReportState>(
  // @ts-ignore
  {
    params: null,
  },
);

export const ReportBookmarkManagerConsumer = ReportBookmarkManagerContext.Consumer;

/**
 * Context provider for the ReportBookmark Panel context.
 *
 * This prevents leaking Panel context details for React components
 */
export function ReportBookmarkManagerProvider(props: {children: React.ReactNode; value: ReportBookmarkManager}) {
  const report: ReportState = {
    get type() {
      return props.value.bookmark.type;
    },

    get params() {
      return props.value.bookmark.params;
    },

    get name() {
      return props.value.bookmark.name;
    },

    get description() {
      return props.value.bookmark.description;
    },

    updateParams: (fn, options: ApplyParamsDetail = {}) => {
      let updatedBookmark: Bookmark = cloneDeep(props.value.bookmark);

      if (typeof fn === `function`) {
        updatedBookmark = produce(updatedBookmark, (draft) => {
          fn(draft.params);
        });
      } else {
        updatedBookmark.params = fn;
      }

      props.value.applyParams(updatedBookmark.params, options);
    },

    isValid(): boolean {
      return hasValidBookmarkParams(props.value.bookmark.params);
    },
  };

  return (
    <ReportBookmarkManagerContext.Provider value={report}>
      <BookmarkDragDropProvider report={report}>{props.children}</BookmarkDragDropProvider>
    </ReportBookmarkManagerContext.Provider>
  );
}

export const useReportBookmark = (): ReportState => {
  const contextValue = React.useContext(ReportBookmarkManagerContext);
  if (contextValue instanceof ReportBookmarkManager) {
    throw new Error(`Attempting to use a Panel context inside a React component`);
  }

  return contextValue;
};
